import cx from "classnames";
import ToggleableAppTag from "src/business-components/ToggleableAppTag/ToggleableAppTag";
import { Tag } from "src/components/general/Tag/Tag";
import WhiteTooltip from "src/components/general/WhiteTooltip/WhiteTooltip";
import { useAutoFitHorizontalNodes } from "src/hooks/useAutoFitHorizontalNodes/useAutoFitHorizontalNodes";
import type { Tag as TagType } from "src/queries/tags/tags.types";
import { getPlural } from "src/utils/string/getPlural";

type PipelineTagsProps = {
  tags: TagType[];
  max?: number;
  className?: string;
  wrap?: boolean;
  clickable?: boolean;
  autofit?: boolean;
  autofitOffset?: number;
  autofitThrottle?: number;
  /** When autofit hides every tag, overrides default `N tags` label */
  overflowAllLabel?: (count: number) => string;
};

const defaultOverflowAllLabel = (count: number) =>
  `${count} ${getPlural(count, "tag")}`;

const overflowLabel = (
  count: number,
  itemCount: number,
  overflowAllLabel?: (count: number) => string
) => {
  if (count >= itemCount) {
    return (overflowAllLabel ?? defaultOverflowAllLabel)(count);
  }
  return `+${count}`;
};

const PipelineTags = ({
  tags,
  max = Infinity,
  className,
  wrap = true,
  clickable = false,
  autofit = false,
  autofitOffset = 0,
  autofitThrottle = 200,
  overflowAllLabel,
}: PipelineTagsProps) => {
  if (autofit) {
    wrap = false;
    max = tags.length;
  }

  const { onMount, onOverflowNodeMount, overflowCount } = useAutoFitHorizontalNodes({
    autofit,
    autofitOffset,
    autofitThrottle,
  });

  const overflowDisplayCount = tags.length + overflowCount - max;

  return tags?.length ? (
    <div
      ref={autofit ? onMount : undefined}
      data-test-id="pipeline-tags"
      className={cx(
        "flex items-center gap-2 max-h-96! overflow-auto",
        autofit &&
          "w-full overflow-hidden! [&>*:not([data-autofit-exclude])]:shrink-0",
        className,
        wrap && "flex-wrap"
      )}
    >
      {tags
        .slice(0, max)
        .map((tag) =>
          clickable ? (
            <ToggleableAppTag key={tag.tag_id} tag={tag.name} tagId={tag.tag_id} />
          ) : (
            <Tag key={tag.tag_id} tag={tag.name} />
          )
        )}
      {(tags.length > max || overflowCount > 0) && (
        <WhiteTooltip
          arrow
          enterDelay={500}
          enterNextDelay={500}
          placement="right-start"
          slotProps={{
            tooltip: {
              className: "max-w-xl!",
            },
          }}
          title={
            <PipelineTags
              tags={tags.slice(Math.max(0, max - overflowCount))}
              clickable={clickable}
            />
          }
          style={{ order: 1 }}
        >
          <span
            ref={onOverflowNodeMount}
            data-autofit-exclude=""
            className="text-xs text-grey-500 cursor-help"
          >
            {overflowLabel(overflowDisplayCount, tags.length, overflowAllLabel)}
          </span>
        </WhiteTooltip>
      )}
    </div>
  ) : null;
};

export default PipelineTags;
